/**
 * ActivityCard — inline HubSpot-style activity row. Collapsed by default
 * (header + 2-line body preview); clicking the chevron or header expands
 * it to show the full body + actions. Edit mode swaps the body for a
 * textarea with Save / Cancel directly on the card (no modal).
 *
 * Designed to render inside the ContactDetail Activities feed and the
 * right-rail Recent Activity card.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { activityDisplay, eventPropertyRows } from '../../utils/activityTitle';
import { sanitizeEmailHtml, useIframeAutoResize } from '../inbox/inboxUtils';
import { isCommunicationEmailRow } from '../../utils/communicationRow';
import { formatRelativeInZone } from '../../lib/timezone';
import useDisplayZone from '../../hooks/useDisplayZone';
import {
    FileText, Phone, Calendar, Mail, MessageCircle, ChevronDown, ChevronUp,
    Edit3, Save, X as XIcon, Loader2, Clock, Zap,
} from 'lucide-react';
import OutreachSendCard from './OutreachSendCard';
import TaskAssignment from './TaskAssignment';
import TaskStateControls from './TaskStateControls';
import ActivityEditHistory from './ActivityEditHistory';
import { followLink } from '../contacts/ContactEventsRail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_VISUAL = {
    note: { Icon: FileText, className: 'text-stone-400' },
    email: { Icon: Mail, className: 'text-blue-500' },
    call: { Icon: Phone, className: 'text-emerald-500' },
    meeting: { Icon: Calendar, className: 'text-amber-500' },
    task: { Icon: FileText, className: 'text-indigo-500' },
    whatsapp: { Icon: MessageCircle, className: 'text-green-500' },
    external_event: { Icon: Zap, className: 'text-violet-500' },
    // An entry from an event's activity log, shown on the guest's contact timeline (#1013, D11).
    event_activity: { Icon: Calendar, className: 'text-violet-500' },
};

// #532 residue — every absolute stamp on the card renders in the viewer's
// display zone WITH its zone label ("Sep 14, 2026 2:12 PM (GMT+8)"), never
// `toLocaleString()` in whatever zone the browser happens to be in. The
// timeline API emits `created_at` without an offset (Mongo drops tzinfo), so
// the shared formatter's parseInstant treats it as the UTC instant it is.
function formatStamp(value, zone) {
    return formatRelativeInZone(value, { zone });
}

// A status-only entry (complete / reopen, #904) is bookkeeping, not an edit to
// the card, so it does not turn on the "edited" chip.
const isContentEdit = (entry) => !(Array.isArray(entry?.changes) && entry.changes.length > 0
    && entry.changes.every((change) => change?.field === 'status'));

/**
 * A guest's entry from an event's activity log, inline in the contact timeline (#1013, OA-3, D11): who did it ("Automatically"
 * for a system actor), the sentence, from → to, and a chip that opens that event's Activity tab. Without `onOpenEvent`
 * (a viewer who cannot open events) the chip is a label.
 */
function EventActivityRow({ activity, displayZone, onOpenEvent }) {
    const system = activity.actor_kind === 'system';
    const actor = system ? 'Automatically' : (activity.actor_name || 'Someone');
    const href = activity.event_id ? `/events/${encodeURIComponent(activity.event_id)}?tab=activity` : null;
    const chipClass = 'inline-flex items-center gap-1 rounded-[5px] border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[11px] font-medium text-violet-700';
    const eventName = activity.event_name || 'Event';
    return (
        <div className="flex items-start gap-3 p-3 border border-stone-200 rounded-lg bg-white" data-activity-id={activity.id} data-testid="event-activity-row">
            <div className="w-7 h-7 rounded-full bg-violet-50 border border-violet-200 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-3.5 h-3.5 text-violet-700" />
            </div>
            <div className="flex-1 min-w-0 space-y-1">
                <p className="text-sm text-stone-800"><span className="font-semibold" data-testid="event-activity-actor">{actor}</span> {activity.text}</p>
                <div className="flex items-center gap-1.5 flex-wrap text-xs">
                    {href && onOpenEvent
                        ? <a href={href} onClick={(event) => followLink(event, href, onOpenEvent)} title="Open this event's activity log" className={`${chipClass} hover:bg-violet-100`} data-testid="event-activity-chip">{eventName}</a>
                        : <span className={chipClass} data-testid="event-activity-chip">{eventName}</span>}
                    {(activity.from_label || activity.to_label) && (
                        <span className="text-stone-600" data-testid="event-activity-change">{activity.from_label ? `${activity.from_label} → ` : ''}{activity.to_label}</span>
                    )}
                    <span className="text-stone-400" data-testid="activity-card-stamp">{formatStamp(activity.date || activity.occurred_at || activity.created_at, displayZone)}</span>
                </div>
            </div>
        </div>
    );
}

export default function ActivityCard({
    activity,
    expanded,
    onToggle,
    onSaved,
    autoFocus = false,
    emailFetchError = false,
    onRetryEmailFetch,
    teamMembers = [],
    onOpenEvent,
}) {
    const { user } = useAuth();
    const displayZone = useDisplayZone();
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(activity?.title || '');
    const [description, setDescription] = useState(activity?.description || activity?.notes || '');
    const [saving, setSaving] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const cardRef = useRef(null);

    useEffect(() => {
        setTitle(activity?.title || '');
        setDescription(activity?.description || activity?.notes || '');
        setEditing(false);
    }, [activity?.activity_id, activity?.id]);

    useEffect(() => {
        if (autoFocus && cardRef.current) {
            cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [autoFocus]);

    // #524 — rules-of-hooks: all hooks (useMemo/useIframeAutoResize) must run
    // BEFORE any early return. If the same card instance switches between an
    // outreach_send task and a normal activity across renders, the hook count
    // would otherwise change and React would throw. useIframeAutoResize guards
    // against a null ref internally, so calling it unconditionally is safe.
    const isEmailRow = isCommunicationEmailRow(activity);
    // #521 residue — the HTML branch keys off CONTENT, not provenance. Gating
    // the sanitise on `isEmailRow` made a single wrong `source` value silently
    // downgrade a formatted email to its truncated preview. ThreadMessage (the
    // inbox reference implementation) uses `Boolean(entry.body_html)`; match it.
    const sanitizedHtml = useMemo(
        () => (activity?.body_html ? sanitizeEmailHtml(activity.body_html) : ''),
        [activity?.body_html],
    );
    // ThreadMessage's F06 guard: body_html that sanitises away to nothing must
    // fall through to the text body instead of rendering an empty iframe. The
    // sanitiser PREPENDS IFRAME_BASE_STYLES, so judge on what survives after
    // the injected <style> — the raw output is never empty.
    const hasHtmlBody = useMemo(
        () => Boolean(sanitizedHtml && sanitizedHtml.replace(/<style[\s\S]*?<\/style>/gi, '').trim()),
        [sanitizedHtml],
    );
    const iframeRef = useRef(null);
    // `expanded` is a dependency because the iframe is mounted inside the
    // `expanded &&` block: collapsing unmounts it and re-expanding creates a
    // fresh element. Keyed only on sanitizedHtml (unchanged across a collapse),
    // the effect never re-ran, so the new iframe got no load listener and no
    // polling — it kept the browser's 150px default and clipped the email.
    useIframeAutoResize(iframeRef, [sanitizedHtml, expanded]);

    if (!activity) return null;

    // Event Outreach send tasks get a bespoke card render (Mark sent /
    // Capture response / Rework next). Regular activities fall through
    // to the generic renderer below.
    if (activity.task_kind === 'outreach_send') {
        return (
            <OutreachSendCard
                activity={activity}
                expanded={expanded}
                onToggle={onToggle}
                onChanged={(next, meta) => onSaved?.(next, meta)}
                teamMembers={teamMembers}
            />
        );
    }

    // An event-log entry has no body to expand or edit: one row (actor and sentence, from → to, the event chip).
    if (activity.type === 'event_activity') {
        return <EventActivityRow activity={activity} displayZone={displayZone} onOpenEvent={onOpenEvent} />;
    }

    const typeKey = (activity.type || 'note').toLowerCase();
    const { Icon, className: typeClass } = TYPE_VISUAL[typeKey] || TYPE_VISUAL.note;

    // Use activity_id; fall back to id for backwards-compat with timeline
    // rows that didn't carry both before the backend update.
    const activityId = activity.activity_id || activity.id;

    const isAuthor = user?.user_id && activity.owner_id === user.user_id;
    const isAdmin = user?.is_admin;
    // A communications row is a real email, not an editable CRM note. Edit
    // prefills the truncated preview and Save PUTs the comm_id to
    // /activities/{id}, which 404s — the row lives in `communications`.
    const canEdit = activity.can_edit !== undefined
        ? Boolean(activity.can_edit)
        : Boolean((isAuthor || isAdmin) && activity.source !== 'communication');

    const body = activity.description || activity.notes || '';
    // The expanded card must show the WHOLE body with its line structure.
    // `description` is html_preview output — whitespace-collapsed and cut at
    // 255 chars with an ellipsis — so it is a last resort, not the answer.
    // `body_text` is the full extracted text (BeautifulSoup get_text with a
    // newline separator) and is what `whitespace-pre-wrap` needs to render
    // the paragraphs the email was written with.
    //
    // Like the HTML branch above, this keys off CONTENT and not `source`.
    // Only the expand fetch puts `body_text` on a row, and it only fires for
    // communication email rows, so gating on the provenance field bought
    // nothing and reintroduced exactly the coupling that caused this bug.
    const expandedBody = activity.body_text || body;
    const { hasTitle, displayTitle, typeText, sourceName } = activityDisplay(activity);
    // #797: an event an app reported through the public API carries its
    // properties; with no body of its own, they are what the card shows.
    const propertyRows = activity.type === 'external_event' ? eventPropertyRows(activity.properties) : [];
    const propertyPreview = propertyRows.map((r) => `${r.key}: ${r.value}`).join(' · ');
    const collapsedPreview = body || propertyPreview;
    const isEdited = Array.isArray(activity.edit_log) && activity.edit_log.some(isContentEdit);

    const handleSave = async () => {
        if (!title.trim()) { toast.error('Title is required'); return; }
        if (!activityId) { toast.error('Cannot edit — missing activity id'); return; }
        setSaving(true);
        try {
            const token = localStorage.getItem('crm_token');
            const res = await axios.put(
                `${API}/activities/${activityId}`,
                { title: title.trim(), description: description.trim() || null },
                { headers: token ? { Authorization: `Bearer ${token}` } : {} },
            );
            toast.success('Saved');
            setEditing(false);
            onSaved?.(res.data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(detail || 'Failed to save activity');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setTitle(activity.title || '');
        setDescription(activity.description || activity.notes || '');
        setEditing(false);
    };

    return (
        <div
            ref={cardRef}
            className="border border-stone-200 rounded-lg bg-white overflow-hidden"
            data-activity-id={activityId}
        >
            {/* ---- Card header (always visible) ---- */}
            <div className="flex items-start gap-3 p-3">
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex-shrink-0 p-0.5 text-stone-400 hover:text-stone-700"
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                >
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className={`w-3.5 h-3.5 ${typeClass}`} />
                </div>
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex-1 min-w-0 text-left"
                >
                    <div className="flex items-center gap-2 flex-wrap">
                        <span data-testid="activity-card-heading" className="text-sm font-semibold text-stone-800 truncate max-w-full">{displayTitle}</span>
                        {hasTitle && (
                            <span data-testid="activity-card-type-chip" className="text-[11px] capitalize text-stone-400 bg-stone-100 rounded px-1.5 py-0.5">{typeText}</span>
                        )}
                        {activity.duration != null && (
                            <span className="text-xs text-stone-500">· {activity.duration} min</span>
                        )}
                        {sourceName && (
                            <span data-testid="activity-card-source" className="text-xs text-stone-500">via {sourceName}</span>
                        )}
                        {(activity.actor?.name || activity.owner_name) && (
                            <span className="text-xs text-stone-500">by {activity.actor?.name || activity.owner_name}</span>
                        )}
                        {isEdited && (
                            <span className="text-[10px] text-stone-400 italic">· edited</span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-stone-400">
                        <Clock className="w-3 h-3" />
                        <span data-testid="activity-card-stamp">{formatStamp(activity.occurred_at || activity.created_at || activity.date, displayZone)}</span>
                    </div>
                </button>
                {canEdit && !editing && (
                    <div className="relative flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setShowActions(s => !s)}
                            className="p-1 text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded"
                            aria-haspopup="menu"
                            aria-expanded={showActions}
                            data-action="activity.actions_menu"
                        >
                            Actions <ChevronDown className="w-3 h-3 inline-block -mt-0.5" />
                        </button>
                        {showActions && (
                            <div
                                className="absolute right-0 top-full mt-1 w-36 bg-white border border-stone-200 rounded-lg shadow-lg z-10 py-1"
                                onMouseLeave={() => setShowActions(false)}
                            >
                                <button
                                    type="button"
                                    onClick={() => { setShowActions(false); setEditing(true); if (!expanded) onToggle?.(); }}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50 text-left"
                                    data-action="activity.edit"
                                >
                                    <Edit3 className="w-3 h-3" /> Edit
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {activity.type === 'task' && (
                <div className="px-3 pb-2 space-y-1.5">
                    <TaskStateControls activity={activity} zone={displayZone} onSaved={onSaved} />
                    <TaskAssignment activity={activity} teamMembers={teamMembers} onSaved={onSaved} compact />
                </div>
            )}

            {/* ---- Collapsed preview: 2-line body snippet ---- */}
            {!expanded && collapsedPreview && (
                <button
                    type="button"
                    onClick={onToggle}
                    className="block w-full text-left px-3 pb-3 -mt-1 text-xs text-stone-500 line-clamp-2 whitespace-pre-wrap hover:text-stone-700"
                >
                    {collapsedPreview}
                </button>
            )}

            {/* ---- Expanded body ---- */}
            {expanded && (
                <div className="px-3 pb-3">
                    {editing ? (
                        <div className="space-y-2">
                            <input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                placeholder="Title"
                                data-testid="activity-card-title"
                            />
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={6}
                                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20 resize-vertical"
                                placeholder="Add details…"
                                autoFocus
                                data-testid="activity-card-description"
                            />
                            <div className="flex items-center gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-md hover:bg-stone-800 disabled:opacity-60"
                                    data-testid="activity-card-save"
                                >
                                    {saving
                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : <Save className="w-3 h-3" />}
                                    Save
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={saving}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-stone-600 hover:text-stone-900"
                                >
                                    <XIcon className="w-3 h-3" /> Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {emailFetchError && isEmailRow ? (
                                <div className="text-sm text-stone-600">
                                    <span className="italic">Couldn't load the full email body.</span>{' '}
                                    {onRetryEmailFetch && (
                                        <button
                                            type="button"
                                            onClick={() => onRetryEmailFetch()}
                                            className="text-stone-900 underline hover:text-stone-700"
                                        >
                                            Retry
                                        </button>
                                    )}
                                </div>
                            ) : hasHtmlBody ? (
                                <iframe
                                    ref={iframeRef}
                                    sandbox="allow-same-origin"
                                    srcDoc={sanitizedHtml}
                                    className="w-full border-0 block"
                                    style={{ minHeight: '64px' }}
                                    title="Email content"
                                />
                            ) : expandedBody ? (
                                <p className="text-sm text-stone-700 whitespace-pre-wrap break-words leading-relaxed">{expandedBody}</p>
                            ) : propertyRows.length === 0 ? (
                                <p className="text-sm text-stone-400 italic">No details recorded.</p>
                            ) : null}
                            {propertyRows.length > 0 && (
                                <dl data-testid="activity-card-properties" className={`grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm ${expandedBody ? 'mt-3' : ''}`}>
                                    {propertyRows.map((r) => (
                                        <React.Fragment key={r.key}>
                                            <dt className="font-mono text-[12px] text-stone-500">{r.key}</dt>
                                            <dd className="text-stone-700 break-words min-w-0">{r.value}</dd>
                                        </React.Fragment>
                                    ))}
                                </dl>
                            )}
                            <ActivityEditHistory activity={activity} displayZone={displayZone} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
